import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { forwardRef, useMemo, useCallback } from 'react';
import { useDebounce } from '@circlefin/hooks';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import isEqual from 'lodash.isequal';
import { Input } from '../Input/Input.es.js';
import { ComboboxOptions } from './Combobox.Options/Combobox.Options.es.js';

function ComboboxCore({ items, searchTerm = '', error, warning, label, labelSuffix, display, placeholder, message, className, debounce = 0, onChange, name, value, disabled, menuLoading = false, inputLoading = false, dropDirection = 'down', onInputChange, onDebouncedInputChange, clearable = false, locale, children, intent = 'default', required, filtered, ...rest }, ref) {
    const debouncedOnInputChange = useDebounce({
        callback: (search, callback)=>callback(search),
        delay: debounce
    });
    /**
     * If label has leading or trailing empty space, it leads to the value not being persisted when you leave the input.
     */ const formattedItems = useMemo(()=>items.map((i)=>({
                ...i,
                label: i.label.trim()
            })), [
        items
    ]);
    const formattedFilteredItems = useMemo(()=>filtered?.map((i)=>({
                ...i,
                label: i.label.trim()
            })), [
        filtered
    ]);
    const derivedFilteredItems = useMemo(()=>{
        if (formattedFilteredItems) return formattedFilteredItems;
        if (searchTerm.length > 0) {
            return formattedItems.filter((item)=>item.label.toUpperCase().includes(searchTerm.toUpperCase()) || item.header === true);
        }
        return formattedItems;
    }, [
        formattedFilteredItems,
        formattedItems,
        searchTerm
    ]);
    // Derived selected item
    const derivedSelectedItem = useMemo(()=>formattedItems.filter((item)=>!item.header).find((item)=>{
            return isEqual(value, item.value);
        }) ?? null, [
        formattedItems,
        value
    ]);
    const { isOpen, inputValue: internalInputValue, selectedItem, toggleMenu, getLabelProps, getMenuProps, getItemProps, getInputProps, getComboboxProps, highlightedIndex, setInputValue: setInternalInputValue } = useCombobox({
        items: derivedFilteredItems,
        selectedItem: derivedSelectedItem,
        onStateChange: ({ selectedItem, type, inputValue, isOpen })=>{
            switch(type){
                case useCombobox.stateChangeTypes.InputBlur:
                    onInputChange('');
                    break;
                case useCombobox.stateChangeTypes.InputChange:
                case useCombobox.stateChangeTypes.FunctionSetInputValue:
                    // Input cleared in state reducer
                    if (!selectedItem && !inputValue) {
                        onChange?.(null);
                    }
                    onInputChange(inputValue ?? '');
                    if (onDebouncedInputChange != null) {
                        debouncedOnInputChange(inputValue ?? '', onDebouncedInputChange);
                    }
                    break;
                case useCombobox.stateChangeTypes.ItemClick:
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                    onChange?.(selectedItem ?? null);
                    onInputChange('');
                    break;
                case useCombobox.stateChangeTypes.FunctionToggleMenu:
                    if (!isOpen) {
                        onInputChange('');
                    }
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                case useCombobox.stateChangeTypes.InputKeyDownHome:
                case useCombobox.stateChangeTypes.InputKeyDownEnd:
                case useCombobox.stateChangeTypes.MenuMouseLeave:
                case useCombobox.stateChangeTypes.ItemMouseMove:
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                case useCombobox.stateChangeTypes.FunctionOpenMenu:
                case useCombobox.stateChangeTypes.FunctionCloseMenu:
                case useCombobox.stateChangeTypes.FunctionSetHighlightedIndex:
                case useCombobox.stateChangeTypes.FunctionSelectItem:
                case useCombobox.stateChangeTypes.FunctionReset:
                case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
                    break;
            }
        },
        itemToString: (item)=>item?.label ?? '',
        stateReducer: (_, actionAndChanges)=>{
            const { type, changes } = actionAndChanges;
            switch(type){
                case useCombobox.stateChangeTypes.InputChange:
                case useCombobox.stateChangeTypes.FunctionSetInputValue:
                    // Clear input
                    if (!changes.inputValue) {
                        return {
                            ...changes,
                            selectedItem: null
                        };
                    }
                    return changes;
                case useCombobox.stateChangeTypes.ItemClick:
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.InputBlur:
                    // On item selected or on blur, set the input value to the selected item
                    return {
                        ...changes,
                        inputValue: changes.selectedItem?.label ?? ''
                    };
                case useCombobox.stateChangeTypes.FunctionToggleMenu:
                    if (!changes.isOpen) {
                        return {
                            ...changes,
                            inputValue: changes.selectedItem?.label ?? ''
                        };
                    }
                    return changes;
                case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                case useCombobox.stateChangeTypes.InputKeyDownHome:
                case useCombobox.stateChangeTypes.InputKeyDownEnd:
                case useCombobox.stateChangeTypes.MenuMouseLeave:
                case useCombobox.stateChangeTypes.ItemMouseMove:
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                case useCombobox.stateChangeTypes.FunctionOpenMenu:
                case useCombobox.stateChangeTypes.FunctionCloseMenu:
                case useCombobox.stateChangeTypes.FunctionSetHighlightedIndex:
                case useCombobox.stateChangeTypes.FunctionSelectItem:
                case useCombobox.stateChangeTypes.FunctionReset:
                case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
                    return changes;
            }
        }
    });
    const handleClearInput = useCallback(()=>setInternalInputValue(''), [
        setInternalInputValue
    ]);
    const showClearButton = clearable && Boolean(internalInputValue);
    /**
     * Use search icon if
     * - Combobox is open OR
     * - Selected item exists and has icon
     * Hide search icon if
     * - Label variant is inline
     * Otherwise
     * - Show selected item icon (if available).
     */ const leftIcon = useMemo(()=>{
        if (display === 'inline') {
            return null;
        }
        return isOpen || selectedItem?.icon == null ? 'SearchOutline' : selectedItem.icon;
    }, [
        isOpen,
        selectedItem,
        display
    ]);
    const comboboxClasses = classNames('cb-combobox', {
        focused: isOpen,
        error: error,
        disabled,
        'no-label': !label,
        'has-clear-button': showClearButton,
        'input-loading': inputLoading
    }, className);
    const menuContainerCss = classNames('combobox-menu-container', `drop-direction-${dropDirection}`, {
        open: isOpen
    });
    return jsx("div", {
        ...getComboboxProps(),
        "aria-required": required,
        className: comboboxClasses,
        "data-testid": "combobox-container",
        title: label,
        ...rest,
        children: jsxs(Input, {
            disabled: disabled,
            display: display,
            emphasize: intent === 'filter',
            error: error,
            label: label,
            labelProps: getLabelProps(),
            labelSuffix: labelSuffix,
            loading: inputLoading,
            message: message,
            placeholder: placeholder,
            value: internalInputValue,
            warning: warning,
            children: [
                jsxs(Input.Field.Text, {
                    onContainerClick: toggleMenu,
                    overlay: selectedItem && !isOpen,
                    ...getInputProps({
                        ref
                    }),
                    name: name,
                    value: internalInputValue,
                    manageCursor: true,
                    children: [
                        leftIcon && jsx(Input.Inline.Icon, {
                            "data-testid": "left-icon",
                            name: leftIcon
                        }),
                        showClearButton && jsx(Input.Inline.IconButton, {
                            align: "right",
                            label: locale?.clearButtonLabel ?? 'clear input',
                            name: "XSolid",
                            onClick: handleClearInput,
                            size: 20
                        }),
                        !isOpen && selectedItem?.tagIcon != null && jsx(Input.Inline.Icon, {
                            align: "right",
                            name: selectedItem.tagIcon
                        }),
                        !isOpen && selectedItem?.tag && jsx(Input.Inline.Affix, {
                            variant: "suffix",
                            children: selectedItem.tag
                        }),
                        jsx(Input.Inline.Icon, {
                            align: "right",
                            className: "caret",
                            name: "ChevronDownSolid",
                            size: 20
                        })
                    ]
                }),
                jsx("div", {
                    className: menuContainerCss,
                    "data-testid": "menu-container",
                    children: /*#__PURE__*/ React__default.isValidElement(children) && /*#__PURE__*/ React__default.cloneElement(children, {
                        getMenuProps,
                        getItemProps,
                        onClearInput: handleClearInput,
                        loading: menuLoading,
                        inputValue: internalInputValue,
                        selected: selectedItem ?? undefined,
                        highlightedIndex,
                        items: derivedFilteredItems
                    })
                })
            ]
        })
    });
}
const ComboboxForwardRef = /*#__PURE__*/ forwardRef(ComboboxCore);
ComboboxForwardRef.displayName = 'Combobox';
ComboboxForwardRef.Options = ComboboxOptions;
ComboboxForwardRef.stateChangeTypes = useCombobox.stateChangeTypes;
const Combobox = ComboboxForwardRef;

export { Combobox, ComboboxForwardRef, Combobox as default };
//# sourceMappingURL=Combobox.es.js.map
